/**
 * WhatsNewModal — displays release notes on first login after a version change.
 * Uses Radix Dialog for accessible modal behavior.
 *
 * Auto-shows when the app version differs from what's stored in localStorage.
 * Can also be opened manually via the exported `useWhatsNew` hook.
 */

import React, { useCallback, useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import changelog from '../data/changelog.json';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChangelogEntry {
  readonly version: string;
  readonly date: string;
  readonly title: string;
  readonly items: readonly { type: string; text: string }[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'coreflow_last_seen_version';
const CURRENT_VERSION = (changelog as ChangelogEntry[])[0]?.version ?? '0.0.0';

// ---------------------------------------------------------------------------
// Hook — exposes open state and version check logic
// ---------------------------------------------------------------------------

export function useWhatsNew(): {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  currentVersion: string;
  hasUnseen: boolean;
} {
  const [isOpen, setIsOpen] = useState(false);

  const lastSeen = typeof window !== 'undefined'
    ? localStorage.getItem(STORAGE_KEY)
    : null;

  const hasUnseen = lastSeen !== CURRENT_VERSION;

  // Auto-show on first render if version is new
  useEffect(() => {
    if (hasUnseen) {
      // Small delay to let the app finish loading
      const timer = setTimeout(() => setIsOpen(true), 500);
      return () => clearTimeout(timer);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const open = useCallback(() => setIsOpen(true), []);

  const close = useCallback(() => {
    setIsOpen(false);
    localStorage.setItem(STORAGE_KEY, CURRENT_VERSION);
  }, []);

  return { isOpen, open, close, currentVersion: CURRENT_VERSION, hasUnseen };
}

// ---------------------------------------------------------------------------
// Type badge component
// ---------------------------------------------------------------------------

function TypeBadge({ type }: { type: string }): React.ReactElement {
  const styles: Record<string, string> = {
    feature: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    improvement: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    fix: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  };

  return (
    <span className={`inline-block text-xs font-medium px-1.5 py-0.5 rounded ${styles[type] ?? 'bg-gray-100 text-gray-800'}`}>
      {type}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Modal Component
// ---------------------------------------------------------------------------

export interface WhatsNewModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
}

export function WhatsNewModal({ isOpen, onClose }: WhatsNewModalProps): React.ReactElement {
  const entries = changelog as ChangelogEntry[];
  const latest = entries[0];
  const previousEntries = entries.slice(1);

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-lg bg-card border border-border shadow-xl p-6 focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          aria-describedby="whats-new-description"
        >
          <Dialog.Title className="text-xl font-semibold text-card-foreground mb-1">
            What's New
          </Dialog.Title>

          {/* Latest version — always expanded */}
          {latest && (
            <>
              <p id="whats-new-description" className="text-sm text-muted-foreground mb-4">
                Version {latest.version} — {latest.date}
              </p>

              <h3 className="text-base font-medium text-card-foreground mb-3">
                {latest.title}
              </h3>

              <ul className="space-y-2.5">
                {latest.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <TypeBadge type={item.type} />
                    <span className="text-sm text-card-foreground leading-snug">{item.text}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Previous versions — collapsible accordion */}
          {previousEntries.length > 0 && (
            <div className="mt-6 border-t border-border pt-4">
              <h4 className="text-sm font-medium text-muted-foreground mb-2">Previous Updates</h4>
              <div className="space-y-2">
                {previousEntries.map((entry) => (
                  <PreviousVersionAccordion key={entry.version} entry={entry} />
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <Dialog.Close asChild>
              <button
                className="bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm font-medium hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring"
              >
                Got it
              </button>
            </Dialog.Close>
          </div>

          <Dialog.Close asChild>
            <button
              className="absolute top-4 right-4 text-muted-foreground hover:text-card-foreground focus:outline-none focus:ring-2 focus:ring-ring rounded"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5" aria-hidden="true">
                <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
              </svg>
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ---------------------------------------------------------------------------
// Accordion for previous versions
// ---------------------------------------------------------------------------

function PreviousVersionAccordion({ entry }: { entry: ChangelogEntry }): React.ReactElement {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="border border-border rounded-md">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-ring rounded-md"
        aria-expanded={isExpanded}
      >
        <span className="font-medium text-card-foreground">
          v{entry.version} — {entry.title}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          aria-hidden="true"
        >
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>
      {isExpanded && (
        <div className="px-3 pb-3">
          <p className="text-xs text-muted-foreground mb-2">{entry.date}</p>
          <ul className="space-y-1.5">
            {entry.items.map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <TypeBadge type={item.type} />
                <span className="text-xs text-card-foreground leading-snug">{item.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
